//@flow
import * as React from 'react';
import { themr } from 'react-css-themr';
import { VirtualizedAsyncCreatableSelect } from 'common/components/core/select';
import { MasterLineItem } from 'common/models/internal/MasterLineItem';
import { translate } from 'react-i18next';
import theme from 'common/entity/generic/LocationCodeInput.scss';
import debounce from 'lodash/debounce';
import { bulkLineItemFetch as bulkLineItemFetchApi } from 'common/middleware/api';
import type { TFunction } from 'react-i18next';

type Props = {
    isLoading: boolean,
    clientId: string,
    disabled: boolean,
    defaultOptionsOnFocus: boolean,
    doubleLine: boolean,
    lineItem: ?MasterLineItem,
    getLineItemFilters: (input: string) => Object,
    theme: Object,
    onChange: (?MasterLineItem | Array<MasterLineItem>) => void,
    t: TFunction,
    selectedLineItem: ?MasterLineItem,
    selectedLineItems: Array<MasterLineItem>,
    creatable: boolean,
    isMulti: boolean,
    placeholder: string,
    testId: string,
    closeMenuOnSelect: boolean,
    formatCreateLabel: (input: string) => string,
    portal: ?boolean
};

type State = {
    lineItems: Array<MasterLineItem>
};

class LineItemCodeInput extends React.Component<Props, State> {
    static defaultProps = {
        doubleLine: true,
        defaultOptionsOnFocus: true,
        portal: false
    };

    constructor(props) {
        super(props);
        this.state = {
            lineItems: []
        };
        this.loadLineItemOptions = debounce(this.loadLineItemOptions, 200);
    }

    loadLineItemOptions = (input: string, callback): void => {
        const { clientId, getLineItemFilters } = this.props;
        const filters = getLineItemFilters(input);
        bulkLineItemFetchApi
            .post({ clientId }, { filters })
            .then((response: Object) => {
                const { lineItems } = response;
                const masterLineItems = lineItems.map(lineItem =>
                    MasterLineItem.fromJS(lineItem)
                );
                this.setState({ lineItems: masterLineItems });
                callback(masterLineItems);
            })
            .catch(() => {
                //Ignore the error
            });
    };

    getOptionValue = (option: MasterLineItem): string => {
        return option.id;
    };

    getOptionLabel = (option: MasterLineItem): string => {
        const { getOptionLabel } = this.props;
        return getOptionLabel?.(option) || option.code;
    };

    getOptionMeta = (option: MasterLineItem): ?string => {
        const { getOptionMeta } = this.props;
        return getOptionMeta ? getOptionMeta(option) : option.name;
    };

    render() {
        const {
            isLoading,
            selectedLineItem,
            selectedLineItems,
            disabled,
            creatable,
            onChange,
            theme,
            isMulti,
            defaultOptions,
            closeMenuOnSelect,
            doubleLine,
            placeholder,
            formatCreateLabel,
            defaultOptionsOnFocus,
            testId,
            portal
        } = this.props;
        const { locations } = this.state;
        return (
            <VirtualizedAsyncCreatableSelect
                testId={testId}
                defaultOptions={defaultOptions}
                defaultOptionsOnFocus={defaultOptionsOnFocus}
                closeMenuOnSelect={closeMenuOnSelect}
                isMulti={isMulti}
                creatable={creatable}
                doubleLine={doubleLine}
                theme={theme}
                cache={false}
                loaderInside={false}
                openMenuOnFocus
                getOptionLabel={this.getOptionLabel}
                getOptionValue={this.getOptionValue}
                getOptionMeta={this.getOptionMeta}
                fallbackLabelKey="code"
                fallbackValueKey="id"
                disabled={disabled}
                isLoading={isLoading}
                formatCreateLabel={formatCreateLabel}
                loadOptions={this.loadLineItemOptions}
                value={selectedLineItem || selectedLineItems}
                placeholder={placeholder}
                onChange={onChange}
                noMarginBottom
                options={locations}
                portal={portal}
            />
        );
    }
}

export default themr(
    null,
    theme
)(translate('entityManagement')(LineItemCodeInput));
